import React, { FC, ReactNode, useEffect, useMemo } from "react";
import { useAtom } from "jotai/index";
import classNames from "classnames";

import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Option } from "components/Select/Select";
import { Divider } from "components/Divider/Divider";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import {
  openedDocItemStateAtom,
  openedDocumentAtom,
} from "../../../atoms/documentsAtom";
import { allItemsAtom } from "../../../atoms/itemAtom";
import { measureUnitsAtom } from "../../../atoms/measureUnitsAtom";
import { allPriceListsAtom } from "../../../atoms/priceListAtom";

import styles from "../styles.module.scss";
interface ComponentProps {}

const ElementContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return <div className={styles.componentContainer}>{children}</div>;
};

export const DocItemModal1: FC<ComponentProps> = ({}): JSX.Element => {
  const [allItems] = useAtom(allItemsAtom);
  const [measureUnits] = useAtom(measureUnitsAtom);
  const [openedDocItemState, setOpenedDocumentState] = useAtom(
    openedDocItemStateAtom
  );
  const [openedDocument] = useAtom(openedDocumentAtom);

  const itemsOptions: Option[] = useMemo(
    () =>
      allItems.map((i) => ({ value: i.itemid, label: i.longname || i.name })),
    [allItems]
  );

  const measureUnitsOptions: Option[] = useMemo(
    () => measureUnits.map((i) => ({ value: i.measureid, label: i.name })),
    [measureUnits]
  );

  const itemVal: Option | undefined = useMemo(
    () =>
      itemsOptions.find((i) => +i.value === Number(openedDocItemState.itemid)),
    [itemsOptions, openedDocItemState.itemid]
  );

  const itemArtikul: string | undefined = useMemo(
    () =>
      allItems.find((i) => +i.itemid === Number(openedDocItemState.itemid))
        ?.code,
    [allItems, openedDocItemState.itemid]
  );

  const measureUnitVal: Option | undefined = useMemo(
    () =>
      measureUnitsOptions.find(
        (i) => +i.value === Number(openedDocItemState.measureid)
      ),
    [measureUnitsOptions, openedDocItemState.measureid]
  );

  const handleItem = (val) => {
    setOpenedDocumentState({
      itemid: val?.value,
      measureid:
        allItems.find((i) => i.itemid === val?.value)?.measureid || null,
    } as any);
    countDefaultPrice(val?.value);
  };

  const handlePrice = (ev) => {
    const objToUpdate: any = { price: ev.target.value };

    objToUpdate.total = String(
      (Number(ev.target.value) * Number(openedDocItemState.amount)).toFixed(2)
    );

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleAmount = (ev) => {
    const objToUpdate: any = { amount: ev.target.value };

    objToUpdate.total = String(
      (Number(ev.target.value) * Number(openedDocItemState.price)).toFixed(2)
    );

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleTotal = (ev) => {
    const objToUpdate: any = { total: ev.target.value };

    objToUpdate.price = String(
      (Number(ev.target.value) / Number(openedDocItemState.amount)).toFixed(2)
    );

    setOpenedDocumentState(objToUpdate as any);
  };

  const countDefaultPrice = (newItemId) => {
    const defaultPrice = (() => {
      const selectedItem = allItems.find((i) => i.itemid === newItemId);
      if (!selectedItem?.prices?.length) return 0;

      const selectedItemDefaultPrice = selectedItem.prices.find(
        (i) => i.pricelistid === openedDocument.store?.pricelistid
      );
      if (!selectedItemDefaultPrice) return 0;
      return selectedItemDefaultPrice.price;
    })();

    handlePrice({ target: { value: defaultPrice } });
  };

  return (
    <div className={styles.wrapper}>
      <ElementContainer>
        <NamedSelect
          options={itemsOptions}
          name={"Товар"}
          nameClassName={styles.inputName}
          className={styles.itemSelect}
          value={itemVal}
          onChange={handleItem}
          placeholder={"Выберите товар"}
        />
      </ElementContainer>
      <div className={styles.inputsRow}>
        <ElementContainer>
          <NamedSelect
            options={measureUnitsOptions}
            name={"Ед. изм"}
            nameClassName={styles.inputName}
            value={measureUnitVal}
            onChange={(val) =>
              setOpenedDocumentState({ measureid: val?.value } as any)
            }
            placeholder={"Выберите ед. изм"}
          />
        </ElementContainer>
        <ElementContainer>
          <NamedInput
            disabled
            value={itemArtikul}
            label={"Артикул"}
            isLabelRegular
            className={styles.artikulInput}
          />
        </ElementContainer>
      </div>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />

      <ElementContainer>
        <NamedInput
          label={"Кол-во"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={openedDocItemState.amount || ""}
          onChange={handleAmount}
        />
      </ElementContainer>
      <ElementContainer>
        <NamedInput
          label={"Цена"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={openedDocItemState.price || ""}
          onChange={handlePrice}
        />
      </ElementContainer>

      <ElementContainer>
        <NamedInput
          label={"Итого"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={openedDocItemState.total || ""}
          onChange={handleTotal}
        />
      </ElementContainer>

      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />

      <ElementContainer>
        <NamedInput
          label={"Комментарий"}
          isLabelRegular
          className={styles.commentInput}
          value={openedDocItemState.itemcomment || ""}
          onChange={(ev) =>
            setOpenedDocumentState({ itemcomment: ev.target.value } as any)
          }
        />
      </ElementContainer>

      <VerticalSpace size={100} />
    </div>
  );
};
